import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';

import { FlexStyle, Platform, StatusBar, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { NetInfoStateType } from '@react-native-community/netinfo';
import { useRoute } from '@react-navigation/native';

import {
    MOBILE_SUBTITLES_EMBEDDED_PLAYER_FONT_SIZE_MAP,
    MOBILE_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE,
    WEB_SUBTITLES_EMBEDDED_PLAYER_FONT_SIZE,
    WEB_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE,
} from 'config/constants.ts';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { setPreviouslyWatchedChannel } from 'store/epg/actions';
import { StreamTypes } from 'store/player/actions.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import parseParentalRating from 'services/data-manager/parsing/utils/parseParentalRating';

import { useCast } from 'utils/cast/useCast';
import usePrevious from 'utils/hooks/usePrevious';
import useRecordings from 'utils/hooks/useRecordings';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { isTablet, isWeb } from 'utils/responsiveness';

import { DetailsRequestUrlType } from 'screens/Detail/types';
import useCloseFullscreenPlayerOnNoInternet from 'screens/Epg/hooks/useCloseFullscreenPlayerOnNoInternet';
import EmbeddedErrorBackground from 'screens/PlayerModal/components/EmbeddedErrorBackground';
import Error from 'screens/PlayerModal/components/Error';
import useParentalControl from 'screens/PlayerModal/hooks/useParentalControl';
import usePlayerSharedBehaviour from 'screens/PlayerModal/hooks/usePlayerSharedBehaviour';
import useVideoEnd from 'screens/PlayerModal/hooks/useVideoEnd';
import playerStyles from 'screens/PlayerModal/styles';
import { RecordingType } from 'screens/PlayerModal/types';

import ConnectionIndicatorAlert from 'components/ConnectionIndicatorAlert';
import useConnectionIndicator from 'components/ConnectionIndicatorAlert/hooks';
import useEmbeddedPlayerOrientation from 'components/EmbeddedPlayer/hooks/useEmbeddedPlayerOrientation';
import NoInternetAlert from 'components/NoInternetAlert';
import Notification from 'components/Notification';
import Player from 'components/Player';
import PlayerOverlay from 'components/PlayerOverlay';
import RecordingsNotificationsAndOverlay from 'components/RecordingsNotificationsAndOverlays';
import useRecordingsNotificationsAndOverlays from 'components/RecordingsNotificationsAndOverlays/hooks/useRecordingsNotificationsAndOverlays';
import useRecordingActionsDialog from 'components/SelectRecordingActions/hooks/useRecordingActionsDialog';

import GenericPinDialog from '../GenericPinDialog';
import EmbeddedPlayerHostPortal from './components/EmbeddedPlayerHostPortal';
import { EMBEDDED_PLAYER_WEB_ID, supportedOrientations } from './constants';
import styles from './styles';
import { EpgPlayerProps, IEmbeddedPlayer } from './types';

const EmbeddedPlayer = forwardRef<IEmbeddedPlayer, EpgPlayerProps>(
    (
        {
            videoItem,
            playbackType,
            style,
            appState,
            navigation,
            fullscreen,
            channel,
            focused,
            templateId,
            subtitlesDialogVisible,
            subtitleButtonPosition,
            primaryColor,
            screenReaderEnabled,
            onSwitchPlayerSource,
            onSubtitlesButtonPress,
            onCloseSubtitlesDialog,
            onSubtitlesModalClosed,
            onSetNotification,
            onShowFullscreen,
            onSetErrorExists,
            onSetPlayerState,
            onCloseFullscreen,
            userRating,
            profileId,
            handleRestoreLiveStream,
        },
        ref,
    ) => {
        const visible = focused;

        const {
            recordingItem,
            recordingIsProtected,
            // single recording data
            isRecordingLoading,
            // series recording data
            seriesId,
            seriesHasProtectedRecording,
            // handlers
            handleCancelRecording,
            handleUnprotectRecording,
            handleDeleteRecording,
            handleDeleteSeriesRecording,
            handleCancelSeriesRecording,
            // dialogs and notifications
            recordingsNotification,
            showQuotaDialog,
            showCancelEpisodeDialog,
            showCancelSeriesDialog,
            resetNotificationCallback,
            hideCancelEpisodeDialogCallback,
            hideCancelSeriesDialogCallback,
            hideQuotaDialogCallback,
            hideDeleteRecordingDialogCallback,
            hideDeleteSeriesDialogCallback,
            // buttons
            recordingsButtonsList,
            onManageRecordingPress,
            // permissions
            hasGlobalRecordingPermission,
        } = useRecordings({
            item: videoItem?.originalProgram,
            type: RecordingType.PLAYER,
        });

        const {
            showConnectionDialog,
            shouldWarnOnMobileData,
            handleShowDialog,
            connectionType,
            selectedConnectionType,
        } = useConnectionIndicator();

        const {
            pinDialogProps,
            onManageRecordingPressCallback,
            handleShowPinDialog,
        } = useRecordingsNotificationsAndOverlays({
            handleDeleteRecording,
            handleUnprotectRecording,
            onManageRecordingPress,
            handleDeleteSeriesRecording,
        });
        const recordingActionsDialogState = useRecordingActionsDialog({
            isRecordingLoading,
            recordingsButtonsList,
            hasGlobalRecordingPermission,
            onManageRecordingPressCallback,
        });

        const {
            jumpToLiveAllowed,
            playerRef,
            isCatchup,
            isVod,
            isNpvr,
            errorExists,
            skipBackwardDisabled,
            skipForwardDisabled,
            muted,
            subtitlesFontSize,
            videoSource,
            showPinDialog,
            timeshiftNotification,
            seeking,
            onVideoLoaded,
            onPlayerReady,
            handleSeeked,
            onPlayerProgress,
            isSOTSPauseDisabled,
            onStatisticsUpdate,
            onSlidingValueChanged,
            onSlidingStart,
            onSlidingCompleted,
            onSlidingCompleteSilently,
            onPlayPausePress,
            onParentalControlChecked,
            handlePlayerError,
            handlePlaying,
            handleSkipForward,
            handleSkipBackward,
            handleJumpToLive,
            handleStartover,
            onParentalCheckDismiss,
            playerError,
            videoDuration,
            playerProgress,
            livePosition,
            playerReady,
            playerPaused,
            isLive,
            isStartover,
            timeshiftModeOn,
            timeshiftPosition,
            timeshiftDuration,
            startoverButtonVisible,
            startoverAllowed,
            pauseAccessLevel,
            rewindAccessLevel,
            fastforwardAccessLevel,
            onResetTimeshiftNotification,
            onStopPlayback,
            disabledNotification,
            onResetDisabledNotification,
            playerTracks,
            selectedAudio,
            selectedSubtitles,
            onTracksChanged,
            onSelectAudio,
            onSelectSubtitles,
            timeshiftIndicatorEnabled,
            onResetPlayerError,
            zapping,
            frameFitting,
            handleSetMuted,
            volume,
            handleChangeVolume,
            playerWaiting,
            handleWaiting,
            handleStopPlayer,
        } = usePlayerSharedBehaviour({
            embedded: true,
            appState,
            channel,
            videoItem,
            fullscreen,
            visible,
            playbackType,
            templateId,
            onSwitchPlayerSource,
            profileId,
            recordingId: recordingItem?.id,
            shouldWarnOnMobileData,
            handleRestoreLiveStream,
            subtitlesDialogVisible,
            recordingActionsDialogVisible:
                recordingActionsDialogState.recordingActionsDialogVisible,
        });

        const route = useRoute();

        const {
            showPinDialog: showDetailPinDialog,
            onShowPinDialog,
            onParentalCheckDismiss: onDetailsParentalCheckDismiss,
            onParentalControlChecked: onDetailsParentalControlChecked,
        } = useParentalControl({
            fullscreen,
            onSetPlayerError: () => null,
            onResetPlayerError,
        });
        const { t } = useTranslation();
        const dispatch = useDispatch();
        const prevFullScreen = usePrevious(fullscreen);
        const { isCastConnected, cast, deviceName } = useCast();

        const [programData, setProgramData] = useState<
            RouteNavigationParams[SCREEN.DETAILS] | null
        >();

        const isContentLocked = needsToLockContent(
            userRating,
            parseParentalRating(videoItem?.originalProgram),
        );

        useEffect(() => {
            onSetErrorExists(errorExists);
        }, [errorExists]);

        const castPlayerOverlayMessage = useMemo(() => {
            return {
                message: `${t('CAST_DIALOG_CONECTED_TO_TITLE')} ${deviceName}`,
            };
        }, [t, deviceName]);

        const ConnectionIndicatorMessage = {
            message: `${t('EPG_PLAYER_INTERNET_CONNECTION_USE_MOBILE_DATA')} `,
        };

        useEffect(() => {
            if (
                isCastConnected &&
                videoItem &&
                playbackType &&
                Platform.OS === 'android'
            ) {
                cast(
                    videoItem,
                    playbackType,
                    playerReady,
                    selectedSubtitles,
                    selectedAudio,
                    playerProgress,
                );

                if (videoItem.channelId) {
                    dispatch(
                        setPreviouslyWatchedChannel({
                            channelId: videoItem.channelId,
                            profileId,
                        }),
                    );
                }
            }
        }, [isCastConnected, videoItem]);

        useEffect(() => {
            if (!visible) {
                if (showPinDialog) {
                    onParentalCheckDismiss();
                }
            }
        }, [onParentalCheckDismiss, visible, showPinDialog]);

        useEmbeddedPlayerOrientation({
            focused,
            appState,
            showPinDialog,
            fullscreen,
            isCastConnected,
            onShowFullscreen,
            onCloseFullscreen,
        });

        useCloseFullscreenPlayerOnNoInternet({
            focused,
            fullscreen,
            appState,
            onCloseFullscreen,
        });

        const handleVideoEnd = useVideoEnd({
            isStartover,
            onJumpToLive: handleJumpToLive,
        });

        const handlePINDismiss = useCallback(() => {
            onParentalCheckDismiss();
            onDetailsParentalCheckDismiss();
            setProgramData(null);
        }, [onParentalCheckDismiss, onDetailsParentalCheckDismiss]);

        useEffect(() => {
            if (!fullscreen) {
                if (timeshiftNotification) {
                    // Set EPG notification and reset modal player notification
                    // to avoid showing it in the fullscreen player modal
                    onSetNotification(timeshiftNotification);
                    onResetTimeshiftNotification();
                }

                if (disabledNotification) {
                    onSetNotification(disabledNotification);
                    onResetDisabledNotification();
                }
            }
        }, [
            fullscreen,
            timeshiftNotification,
            onResetTimeshiftNotification,
            disabledNotification,
            onResetDisabledNotification,
            onSetNotification,
        ]);

        const navigateToDetailPage = useCallback(() => {
            if (videoItem && programData && navigation) {
                dispatch(
                    startSentryTransaction(
                        SENTRY_TRANSACTIONS.DetailPageRendering,
                    ),
                );
                navigation.navigate(SCREEN.DETAILS, programData);

                setProgramData(null);
            }
        }, [programData, videoItem, navigation]);

        const getFilterType = useCallback(() => {
            switch (playbackType) {
                case StreamTypes.live:
                case StreamTypes.startover:
                case StreamTypes.catchup:
                case StreamTypes.npvr:
                    return DetailsRequestUrlType.PROGRAM;

                default:
                    return DetailsRequestUrlType.VOD;
            }
        }, [playbackType]);

        const handleOpenDetails = useCallback(() => {
            if (videoItem && navigation) {
                let params: RouteNavigationParams[SCREEN.DETAILS] = {
                    templateId,
                    filterType: getFilterType(),
                    id: videoItem.originalProgram?.id,
                    channelId: videoItem.channelId,
                };

                if (videoItem.seriesId) {
                    params = {
                        ...params,
                        seriesId: videoItem.seriesId,
                        season: videoItem.seasonNumber,
                        episode: videoItem.episodeNumber,
                    };
                }

                onCloseFullscreen();
                setProgramData(params);
            }
        }, [
            navigation,
            videoItem,
            templateId,
            onCloseFullscreen,
            isContentLocked,
            navigateToDetailPage,
            playbackType,
        ]);

        const handleParentalControlChecked = useCallback(() => {
            if (programData) {
                onDetailsParentalControlChecked();
                navigateToDetailPage();
            } else {
                onParentalControlChecked();
            }
        }, [
            navigateToDetailPage,
            onParentalControlChecked,
            onDetailsParentalControlChecked,
            programData,
        ]);

        useEffect(() => {
            if (isContentLocked && videoItem) {
                handleOpenDetails();

                if (videoItem.channelId) {
                    dispatch(
                        setPreviouslyWatchedChannel({
                            channelId: videoItem.channelId,
                            profileId,
                        }),
                    );
                }
            }
        }, [isContentLocked, videoItem]);

        useEffect(() => {
            if (!focused) {
                onStopPlayback();
                handleStopPlayer();
                onSetPlayerState({
                    videoItem: undefined,
                    playbackType: StreamTypes.live,
                });
            }
        }, [focused]);

        useEffect(
            () => () => {
                onCloseFullscreen();
                onStopPlayback();
                onSetPlayerState({
                    videoItem: undefined,
                    playbackType: StreamTypes.live,
                });
            },
            [],
        );

        useEffect(() => {
            if (programData) {
                setTimeout(() => {
                    if (isContentLocked) {
                        onShowPinDialog();
                    } else {
                        navigateToDetailPage();
                    }
                }, 250);
            }
        }, [programData]);

        useImperativeHandle(
            ref,
            () => ({
                stopPlayback: () => onStopPlayback({ resetSource: false }),
            }),
            [onStopPlayback],
        );

        const playerStyle = useMemo(() => {
            const baseStyle = {
                width: '100%',
                height: '100%',
            };

            if (isTablet && Platform.OS === 'android' && !fullscreen) {
                return {
                    ...baseStyle,
                    ...styles.embeddedTabletPlayer,
                };
            }

            if (!isWeb && fullscreen) {
                return playerReady && frameFitting.frameFittingOn
                    ? playerStyles.fitPlayer
                    : playerStyles.player;
            }

            if (isWeb) {
                return [
                    styles.embeddedWebPlayer,
                    {
                        fontSize: fullscreen
                            ? WEB_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE
                            : WEB_SUBTITLES_EMBEDDED_PLAYER_FONT_SIZE,
                    },
                ];
            }

            return baseStyle;
        }, [fullscreen, playerReady, frameFitting.frameFittingOn]);

        useEffect(() => {
            if (
                connectionType === NetInfoStateType.cellular &&
                selectedConnectionType === NetInfoStateType.wifi &&
                !showConnectionDialog &&
                videoItem
            ) {
                handleShowDialog();
            }
        }, [videoItem]);

        return (
            <EmbeddedPlayerHostPortal
                id={`${EMBEDDED_PLAYER_WEB_ID}${route.key}`}
                portalStyle={style}
                fullscreen={fullscreen}
            >
                <View
                    style={[
                        styles.safeArea,
                        !fullscreen && isWeb && styles.safeAreaEmbeddedWeb,
                    ]}
                >
                    <StatusBar translucent={fullscreen} hidden={fullscreen} />
                    {(isCastConnected && Platform.OS === 'android') ||
                    shouldWarnOnMobileData ? (
                        <>
                            <EmbeddedErrorBackground />
                            <Error
                                error={
                                    shouldWarnOnMobileData
                                        ? ConnectionIndicatorMessage
                                        : castPlayerOverlayMessage
                                }
                                fullscreen={fullscreen}
                                boldTitle={false}
                            />
                        </>
                    ) : (
                        <>
                            {!focused ? (
                                <></>
                            ) : (
                                <Player
                                    ref={playerRef}
                                    source={videoSource}
                                    style={playerStyle as FlexStyle}
                                    muted={muted}
                                    volume={volume}
                                    webSubtitleFontSize={subtitlesFontSize}
                                    mobileSubtitleFontSize={
                                        (fullscreen
                                            ? MOBILE_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE
                                            : MOBILE_SUBTITLES_EMBEDDED_PLAYER_FONT_SIZE_MAP)[
                                            subtitlesFontSize
                                        ]
                                    }
                                    onPlayerSourceLoaded={onVideoLoaded}
                                    onReady={onPlayerReady}
                                    onProgress={onPlayerProgress}
                                    onSeeked={handleSeeked}
                                    onEnd={handleVideoEnd}
                                    onError={handlePlayerError}
                                    onPlaying={handlePlaying}
                                    onWaiting={handleWaiting}
                                    onStatisticsUpdate={onStatisticsUpdate}
                                    onTracksChanged={onTracksChanged}
                                />
                            )}
                            <PlayerOverlay
                                embedded
                                zapping={zapping}
                                frameFitting={frameFitting}
                                fullscreen={fullscreen}
                                onFullscreenPress={onShowFullscreen}
                                onMinimizePress={onCloseFullscreen}
                                onCloseModal={onCloseFullscreen}
                                playerError={playerError}
                                videoItem={videoItem}
                                duration={videoDuration}
                                position={playerProgress}
                                muted={muted}
                                volume={volume}
                                livePosition={livePosition}
                                readyToPlay={playerReady}
                                playerWaiting={playerWaiting}
                                isPaused={playerPaused}
                                isSOTSPauseDisabled={isSOTSPauseDisabled}
                                isLive={isLive}
                                isStartover={isStartover}
                                isCatchup={isCatchup}
                                isVod={isVod}
                                isNpvr={isNpvr}
                                skipForwardDisabled={skipForwardDisabled}
                                skipBackwardDisabled={skipBackwardDisabled}
                                primaryColor={primaryColor}
                                airTimeStart={videoItem?.startTime}
                                airTimeEnd={videoItem?.endTime}
                                seeking={seeking}
                                timeshiftModeOn={timeshiftModeOn}
                                timeshiftPosition={timeshiftPosition}
                                timeshiftDuration={timeshiftDuration}
                                jumpToLiveAllowed={jumpToLiveAllowed}
                                startoverAllowed={
                                    startoverAllowed && !errorExists
                                }
                                startoverButtonVisible={
                                    startoverButtonVisible && !errorExists
                                }
                                pauseAccessLevel={pauseAccessLevel}
                                rewindAccessLevel={rewindAccessLevel}
                                fastforwardAccessLevel={fastforwardAccessLevel}
                                playerTracks={playerTracks}
                                selectedAudio={selectedAudio}
                                selectedSubtitles={selectedSubtitles}
                                subtitlesDialogVisible={subtitlesDialogVisible}
                                subtitleButtonPosition={subtitleButtonPosition}
                                onSubtitlesButtonPress={onSubtitlesButtonPress}
                                onCloseSubtitlesDialog={onCloseSubtitlesDialog}
                                onSubtitlesModalClosed={onSubtitlesModalClosed}
                                onPlayPausePress={onPlayPausePress}
                                onSlidingStart={onSlidingStart}
                                onSkipForward={handleSkipForward}
                                onSkipBackward={handleSkipBackward}
                                onSlidingComplete={onSlidingCompleted}
                                onSlidingCompleteSilently={
                                    onSlidingCompleteSilently
                                }
                                onSlidingValueChanged={onSlidingValueChanged}
                                onJumpToLive={handleJumpToLive}
                                onStartover={handleStartover}
                                onSelectAudio={onSelectAudio}
                                onSelectSubtitles={onSelectSubtitles}
                                onOpenDetails={handleOpenDetails}
                                timeshiftIndicatorEnabled={
                                    timeshiftIndicatorEnabled
                                }
                                routeKey={route.key}
                                recordingActionsDialogState={
                                    recordingActionsDialogState
                                }
                                recordingItem={recordingItem}
                                screenReaderEnabled={screenReaderEnabled}
                                onToggleMute={handleSetMuted}
                                onChangeVolume={handleChangeVolume}
                            />
                        </>
                    )}
                </View>
                {fullscreen && (
                    <>
                        <RecordingsNotificationsAndOverlay
                            isRecordingLoading={isRecordingLoading}
                            seriesId={seriesId}
                            handleCancelRecording={handleCancelRecording}
                            handleCancelSeriesRecording={
                                handleCancelSeriesRecording
                            }
                            recordingsNotification={recordingsNotification}
                            showQuotaDialog={showQuotaDialog}
                            showCancelEpisodeDialog={showCancelEpisodeDialog}
                            showCancelSeriesDialog={showCancelSeriesDialog}
                            resetNotificationCallback={
                                resetNotificationCallback
                            }
                            hideCancelEpisodeDialogCallback={
                                hideCancelEpisodeDialogCallback
                            }
                            hideCancelSeriesDialogCallback={
                                hideCancelSeriesDialogCallback
                            }
                            hideDeleteRecordingDialogCallback={
                                hideDeleteRecordingDialogCallback
                            }
                            hideDeleteSeriesDialogCallback={
                                hideDeleteSeriesDialogCallback
                            }
                            hideQuotaDialogCallback={hideQuotaDialogCallback}
                            pinDialogProps={pinDialogProps}
                            handleShowPinDialog={handleShowPinDialog}
                            playerInFullscreen={fullscreen}
                            recordingIsProtected={recordingIsProtected}
                            seriesHasProtectedRecording={
                                seriesHasProtectedRecording
                            }
                            onDeleteRecording={handleDeleteRecording}
                            onDeleteSeriesRecording={
                                handleDeleteSeriesRecording
                            }
                            onOpenDetails={handleOpenDetails}
                            fromPlayer
                        />
                        <Notification
                            fromPlayer
                            notification={timeshiftNotification}
                            onResetNotification={onResetTimeshiftNotification}
                        />
                        <Notification
                            fromPlayer
                            notification={disabledNotification}
                            onResetNotification={onResetDisabledNotification}
                        />
                    </>
                )}
                <GenericPinDialog
                    modal
                    appState={appState}
                    visible={
                        (showPinDialog || showDetailPinDialog) &&
                        !isCastConnected
                    }
                    onClose={handlePINDismiss}
                    onSuccess={handleParentalControlChecked}
                    wasFullScreen={prevFullScreen && !fullscreen}
                />
                {focused && (
                    <ConnectionIndicatorAlert
                        supportedOrientation={supportedOrientations}
                        handleClosePlayer={onCloseFullscreen}
                    />
                )}
                {fullscreen && (
                    <NoInternetAlert
                        supportedOrientation={supportedOrientations}
                    />
                )}
            </EmbeddedPlayerHostPortal>
        );
    },
);

export default EmbeddedPlayer;
